import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { AgGridReact } from "ag-grid-react";
import { Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import { ToastContainer, toast, Zoom } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import baseURL from "../../Services/Url";
import FirstNavbar from "../dashboard/FirstNavbar";
import SideBar from "../dashboard/SideBar";

const emptyForm = {
  name: "",
  url: "",
  altText: "",
  entityType: "",
  entityId: "",
};

function MediaManager() {
  const [items, setItems] = useState([]);
  const [loader, setLoader] = useState(false);
  const [uploadLoader, setUploadLoader] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [uploadForm, setUploadForm] = useState({
    file: null,
    name: "",
    altText: "",
    folder: "media",
    entityType: "",
    entityId: "",
  });

  const fetchItems = useCallback(() => {
    setLoader(true);
    axios
      .get(`${baseURL}/media`)
      .then((res) => {
        setItems(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || "Unable to load media");
      })
      .finally(() => setLoader(false));
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const updateUploadField = (name, value) => {
    setUploadForm((prev) => ({ ...prev, [name]: value }));
  };

  const updateFormField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpload = () => {
    if (!uploadForm.file) {
      toast.error("Please select a file");
      return;
    }

    const formData = new FormData();
    formData.append("file", uploadForm.file);
    formData.append("name", uploadForm.name);
    formData.append("altText", uploadForm.altText);
    formData.append("folder", uploadForm.folder || "media");
    formData.append("entityType", uploadForm.entityType);
    formData.append("entityId", uploadForm.entityId);

    setUploadLoader(true);
    axios
      .post(`${baseURL}/media/upload`, formData)
      .then(() => {
        toast.success("Media uploaded");
        setUploadForm({
          file: null,
          name: "",
          altText: "",
          folder: "media",
          entityType: "",
          entityId: "",
        });
        fetchItems();
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || "Unable to upload media");
      })
      .finally(() => setUploadLoader(false));
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setForm({
      name: item.name || "",
      url: item.url || "",
      altText: item.altText || "",
      entityType: item.entityType || "",
      entityId: item.entityId || "",
    });
    setShowForm(true);
  };

  const saveMedia = () => {
    if (!editingItem?.id) return;

    setLoader(true);
    axios
      .put(`${baseURL}/media/${editingItem.id}`, form)
      .then(() => {
        toast.success("Media updated");
        setShowForm(false);
        setEditingItem(null);
        setForm(emptyForm);
        fetchItems();
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || "Unable to update media");
      })
      .finally(() => setLoader(false));
  };

  const confirmDelete = () => {
    if (!deleteItem?.id) return;

    setLoader(true);
    axios
      .delete(`${baseURL}/media/${deleteItem.id}`)
      .then(() => {
        toast.success("Media deleted");
        setDeleteItem(null);
        fetchItems();
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || "Unable to delete media");
      })
      .finally(() => setLoader(false));
  };

  const columnDefs = [
    {
      headerName: "Id",
      valueGetter: "node.rowIndex + 1",
      maxWidth: 90,
      filter: false,
    },
    {
      headerName: "Preview",
      field: "url",
      minWidth: 120,
      filter: false,
      cellRendererFramework: (params) =>
        params.data.url ? (
          <img
            src={params.data.url}
            alt={params.data.altText || params.data.name || "Media"}
            style={{ width: 56, height: 56, objectFit: "cover" }}
          />
        ) : null,
    },
    { headerName: "Name", field: "name", minWidth: 180 },
    { headerName: "Alt Tag", field: "altText", minWidth: 220 },
    { headerName: "Type", field: "mimeType", minWidth: 140 },
    { headerName: "Entity Type", field: "entityType", minWidth: 140 },
    { headerName: "Entity ID", field: "entityId", minWidth: 160 },
    {
      headerName: "Update",
      minWidth: 120,
      filter: false,
      cellRendererFramework: (params) => (
        <Button
          variant="outline-primary"
          size="sm"
          type="button"
          onClick={() => openEdit(params.data)}
        >
          Edit
        </Button>
      ),
    },
    {
      headerName: "Delete",
      minWidth: 120,
      filter: false,
      cellRendererFramework: (params) => (
        <Button
          variant="outline-danger"
          size="sm"
          type="button"
          onClick={() => setDeleteItem(params.data)}
        >
          Delete
        </Button>
      ),
    },
  ];

  return (
    <div>
      <ToastContainer
        position="top-center"
        autoClose={1000}
        hideProgressBar
        transition={Zoom}
        delay={500}
        limit={1}
      />
      <div className="sidebar">
        <SideBar />
      </div>

      <div className="content">
        <div className="container">
          <FirstNavbar />
          <div className="row my-2 align-items-center">
            <div className="col-md-6">
              <h3>
                <b>Media Manager</b>
              </h3>
            </div>
          </div>

          <div className="border rounded p-3 mb-4">
            <Row className="align-items-end">
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>File</Form.Label>
                  <Form.Control
                    type="file"
                    onChange={(event) => updateUploadField("file", event.target.files[0])}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={uploadForm.name}
                    onChange={(event) => updateUploadField("name", event.target.value)}
                    placeholder="Media name"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Alt Tag</Form.Label>
                  <Form.Control
                    type="text"
                    value={uploadForm.altText}
                    onChange={(event) => updateUploadField("altText", event.target.value)}
                    placeholder="Image alt text"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="align-items-end">
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Folder</Form.Label>
                  <Form.Control
                    type="text"
                    value={uploadForm.folder}
                    onChange={(event) => updateUploadField("folder", event.target.value)}
                    placeholder="media"
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Entity Type</Form.Label>
                  <Form.Control
                    type="text"
                    value={uploadForm.entityType}
                    onChange={(event) => updateUploadField("entityType", event.target.value)}
                    placeholder="blogs, projects, clients"
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Entity ID</Form.Label>
                  <Form.Control
                    type="text"
                    value={uploadForm.entityId}
                    onChange={(event) => updateUploadField("entityId", event.target.value)}
                    placeholder="Related item ID"
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Button
                  variant="primary"
                  type="button"
                  className="w-100 mb-3"
                  onClick={handleUpload}
                  disabled={uploadLoader}
                >
                  {uploadLoader ? <Spinner animation="border" size="sm" /> : "Upload"}
                </Button>
              </Col>
            </Row>
          </div>

          <div className="ag-theme-alpine" style={{ height: "65vh", width: "100%" }}>
            <AgGridReact
              rowHeight={62}
              columnDefs={columnDefs}
              defaultColDef={{
                sortable: true,
                filter: true,
                floatingFilter: true,
                flex: 1,
                resizable: true,
                minWidth: 120,
              }}
              pagination={true}
              paginationPageSize={10}
              rowData={items}
            />
          </div>
        </div>
      </div>

      <Modal show={showForm} onHide={() => setShowForm(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Media</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type="text"
                  value={form.name}
                  onChange={(event) => updateFormField("name", event.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Alt Tag</Form.Label>
                <Form.Control
                  type="text"
                  value={form.altText}
                  onChange={(event) => updateFormField("altText", event.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label>URL</Form.Label>
            <Form.Control
              type="text"
              value={form.url}
              onChange={(event) => updateFormField("url", event.target.value)}
            />
          </Form.Group>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Entity Type</Form.Label>
                <Form.Control
                  type="text"
                  value={form.entityType}
                  onChange={(event) => updateFormField("entityType", event.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Entity ID</Form.Label>
                <Form.Control
                  type="text"
                  value={form.entityId}
                  onChange={(event) => updateFormField("entityId", event.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
          {form.url && (
            <img
              src={form.url}
              alt={form.altText || form.name || "Media"}
              style={{ width: 140, height: 100, objectFit: "cover" }}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" type="button" onClick={() => setShowForm(false)}>
            Cancel
          </Button>
          <Button variant="primary" type="button" onClick={saveMedia} disabled={loader}>
            {loader ? <Spinner animation="border" size="sm" /> : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(deleteItem)} onHide={() => setDeleteItem(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Media</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this media item?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" type="button" onClick={() => setDeleteItem(null)}>
            Cancel
          </Button>
          <Button variant="danger" type="button" onClick={confirmDelete} disabled={loader}>
            {loader ? <Spinner animation="border" size="sm" /> : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default MediaManager;
